/**
 * `GET /v1/me/bootstrap` — appskallets seks første GET-er i ett kall, lagt rett i cachen.
 *
 * Hver fulle sidelast stilte `/me`, `/me/profile`, `/me/concerts`, uleste tråder, uleste varsler og
 * festivallista i parallell, og hver kostet 150–250 ms i testmiljøet — mest overhead per
 * forespørsel, og alle seks sloss om de samme åtte forbindelsene. Her er de én.
 *
 * DELENE LEGGES UNDER NØKLENE KROKENE ALLEREDE LESER, hentet fra de samme `*QueryOptions` som
 * krokene bruker — aldri en bokstavelig nøkkel. Da virker hver krok uendret, og `setQueryData`
 * stempler svaret som ferskt, så ingen av dem henter på nytt ved montering innenfor sin egen
 * `staleTime`. Ugyldiggjøring, sanntidsstrømmen og cachenivåene er de samme som før.
 *
 * EN `null`-DEL LEGGES IKKE. Den betyr «spør selv» (cella mangler, ingen aktør, gjest), og en
 * krok som trenger tallet henter det da som før.
 */
import { meBootstrapResponseSchema, type MeResponse } from '@tvitvi/shared';
import type { QueryClient } from '@tanstack/react-query';

import { apiRequest } from '@/api/client';
import { festivalsQueryOptions } from '@/api/queries/festivals';
import { meQueryOptions } from '@/api/queries/me';
import { myProfileQueryOptions } from '@/api/queries/my-profile';
import { homeOrganizationIdFor } from '@/auth/active-org';
import { myConcertsQueryOptions } from '@/features/concerts/concert-queries';
import { inboxUnreadCountsQueryOptions } from '@/features/notifications/queries';
import { unreadCountQueryOptions } from '@/features/overview/queries';
import { applyLocale } from '@/i18n';

/*
 * ÉN HENTING OM GANGEN PER KLIENT. Ruteren kjører `beforeLoad` på nytt for en forhåndslasting
 * (`defaultPreload: 'intent'`), og en mus over en lenke mens den første sidelasten ennå venter ville
 * ellers sendt det samme kallet to ganger.
 */
const inFlight = new WeakMap<QueryClient, Promise<MeResponse>>();

export function loadShellBootstrap(queryClient: QueryClient): Promise<MeResponse> {
  const pending = inFlight.get(queryClient);
  if (pending) return pending;
  const run = fetchAndSeed(queryClient).finally(() => inFlight.delete(queryClient));
  inFlight.set(queryClient, run);
  return run;
}

async function fetchAndSeed(queryClient: QueryClient): Promise<MeResponse> {
  const bootstrap = await apiRequest({ path: '/me/bootstrap', schema: meBootstrapResponseSchema });
  const { me } = bootstrap;
  /* Det samme `meQueryOptions` gjør med hver ferske `me`: språket følger `users.locale`. */
  await applyLocale(me.user.locale);

  queryClient.setQueryData(meQueryOptions().queryKey, me);
  queryClient.setQueryData(myProfileQueryOptions().queryKey, bootstrap.profile);
  if (bootstrap.concerts !== null) {
    queryClient.setQueryData(myConcertsQueryOptions().queryKey, bootstrap.concerts);
  }

  const organizationId = me.activeOrganizationId;
  if (organizationId !== null) {
    if (bootstrap.inboxUnreadCounts !== null) {
      queryClient.setQueryData(
        inboxUnreadCountsQueryOptions(organizationId).queryKey,
        bootstrap.inboxUnreadCounts,
      );
    }
    if (bootstrap.festivals !== null) {
      queryClient.setQueryData(festivalsQueryOptions(organizationId).queryKey, bootstrap.festivals);
    }
  }
  /* Klokka nøkler på hjemmet (`homeOrganizationIdFor`), som utenfor gjestemodus er det aktive. */
  const bellOrganizationId = homeOrganizationIdFor(me);
  if (bellOrganizationId !== null && bootstrap.notificationUnreadCount !== null) {
    queryClient.setQueryData(
      unreadCountQueryOptions(bellOrganizationId).queryKey,
      bootstrap.notificationUnreadCount,
    );
  }
  return me;
}
